import { Field } from '@/components/ui/field';
import { FileUp } from 'lucide-react';
import { type ChangeEvent, type ReactNode, useState } from 'react';

/** GCP 서비스계정 JSON 의 식별 필드 (파싱 성공 시 호출부로 전달). */
export interface ServiceAccountParsed {
  project_id: string;
  client_email: string;
}

interface ServiceAccount {
  type?: string;
  project_id?: string;
  client_email?: string;
}

interface ServiceAccountJsonUploadProps {
  id: string;
  label: string;
  hint?: string;
  /** 외부(폼 레벨) 에러. 내부 파싱 에러보다 우선 표시. */
  error?: string;
  /** 파일 선택 버튼에 표시할 기본 문구. 파일 선택 후엔 파일명으로 대체. */
  placeholder?: string;
  /** 파싱·검증 성공 시 — 원본 JSON 문자열과 식별 필드를 전달. */
  onLoaded: (rawJson: string, parsed: ServiceAccountParsed) => void;
  /** 파싱·검증 실패 시 — 호출부에서 credentials 를 비우는 등 처리. */
  onError?: (message: string) => void;
  /** 입력란 아래 요약/상태 (예: ✓ project · email, 또는 "현재 …"). */
  summary?: ReactNode;
}

/**
 * GCP 서비스계정 JSON 키 파일 업로드 컴포넌트.
 *
 * BigQuery 데이터소스와 GCP Vertex AI 엔드포인트가 동일하게 사용 — 파일을 읽어
 * `type === 'service_account'` & `project_id` 를 검증하고, 원본 JSON 문자열을 그대로
 * 호출부로 넘긴다(서버는 이 문자열을 암호화 저장). 파일명/파싱에러는 내부에서 관리하고,
 * 입력란 아래 요약은 호출부가 `summary` 로 제어한다.
 */
export function ServiceAccountJsonUpload({
  id,
  label,
  hint,
  error,
  placeholder = 'JSON 파일 선택…',
  onLoaded,
  onError,
  summary,
}: ServiceAccountJsonUploadProps) {
  const [fileName, setFileName] = useState<string | null>(null);
  const [saError, setSaError] = useState<string | null>(null);

  const onFile = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result ?? '');
      try {
        const sa = JSON.parse(text) as ServiceAccount;
        if (sa.type !== 'service_account' || !sa.project_id) {
          throw new Error('service_account 타입의 JSON 이 아닙니다.');
        }
        setFileName(file.name);
        setSaError(null);
        onLoaded(text, { project_id: sa.project_id, client_email: sa.client_email ?? '' });
      } catch (err) {
        setFileName(file.name);
        const msg = err instanceof Error ? err.message : 'JSON 파싱 실패';
        setSaError(msg);
        onError?.(msg);
      }
    };
    reader.readAsText(file);
  };

  return (
    <Field label={label} htmlFor={id} error={error ?? saError ?? undefined} hint={hint}>
      <label className="flex cursor-pointer items-center gap-2 rounded-md border border-border bg-surface px-3 py-2 text-sm hover:border-accent">
        <FileUp className="size-4 text-foreground-muted" />
        <span className="text-foreground-muted">{fileName ?? placeholder}</span>
        <input
          id={id}
          type="file"
          accept=".json,application/json"
          onChange={onFile}
          className="hidden"
        />
      </label>
      {summary}
    </Field>
  );
}
